/**
 * OrgSwitcher.tsx — Organization switching UI (WEB-281.4 P2 tenancy FE).
 *
 * WEB-3739 юнит 1: разметка — строки окна меню профиля нового вида (`MeMenu`,
 * эталон `design/cabinet-rescin/app.jsx`, `MeMenu`): подпись «Организации»,
 * строки `li.opt` (`menuitemradio`, имя, под ним роль и «· N проектов»),
 * «Создать рабочее пространство». Отдаёт `li` — их
 * кладёт в свой `ul.pop.menu` меню профиля. Клавиатура (↓ ↑ Enter) — у меню
 * (`useMenuKeys` кита): своего обработчика клавиш у строк нет, иначе Enter
 * сработал бы дважды. Логика переключения, предел, подтверждение ухода,
 * сообщения об итоге — прежние.
 *
 * WEB-3796: итог смены — всплывашка кита (`useToast`): «переключено» — `ok`,
 * «переключено, но выбор не сохранён» — `warn`, отказ — `err`. Узел всплывашки
 * держит хозяин меню (`MeMenu`) и отдаёт функцию показа `onToast`: строки
 * переключателя живут внутри окна меню, а меню закрывается ДО ответа сервера —
 * свой узел здесь пропал бы вместе с ними, и итог никто бы не увидел.
 *
 * WEB-3758 (решение № 46, владелец 28.10; эталон `MeMenu` app.jsx:291–327):
 * текущая организация — первой, остальные в прежнем порядке; от шести
 * организаций над списком — поиск «Найти организацию» по имени (без учёта
 * регистра и пробелов по краям), пусто — «Ничего не нашлось»; строки лежат
 * в `li.me-orgs > ul[role=group]` со своей прокруткой (140 px, стили —
 * `cabinet-shell.css`), чтобы пункты ниже были видны всегда. Поиск живёт,
 * пока открыто окно: меню убирает переключатель вместе с окном. На пределе
 * своих организаций пункт «Создать» прежний с виду, а нажатие закрывает меню
 * и показывает причину отказа всплывашкой ошибки (`onToast(…, 'err')`,
 * владелец 27.10 — вместо погашенного пункта с причиной под ним, WEB-1564).
 * Сервер остаётся решающим слоем.
 *
 * Props:
 *   onCreateClick — called when "Create workspace" is clicked
 *   onSelect — строку организации нажали или «Создать» на пределе (меню
 *     закрывается до перехода или до всплывашки отказа)
 *   onToast — показать итог смены всплывашкой кита (узел — у хозяина меню)
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/kit/ds';
import type { ToastKind } from '@/components/kit/ds';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { switchActiveOrgThunk, setActiveOrg } from '@/store/organizationsSlice';
import {
  clearActiveProjectThunk,
  fetchProjectsThunk,
} from '@/store/projectsSlice';
import { fetchBillingThunk } from '@/store/billingSlice';
import { MAX_ORGS_PER_USER } from '@/api/serverErrorMessages';
import { useStrings } from '@/i18n';
import { settingsStrings } from '@/i18n/dictionaries/settings';
import { commonStrings } from '@/i18n/dictionaries/common';
import { localizeError } from '@/utils/localizeError';
import { confirmLeave } from '@/utils/leaveGuard';
import { orgModeLanding } from '@/router/landingRoute';

interface OrgSwitcherProps {
  onCreateClick: () => void;
  onSelect?: () => void;
  onToast: (text: string, kind: ToastKind) => void;
}

// WEB-641 A3: cabinet-mockup показывает роль у КАЖДОЙ орги (Владелец/Администратор/
// Участник проекта), не только у project-only орг. `member` сохраняет свой сигнал
// (объяснение, почему org-settings/team недоступны — WEB-562 A0).
/** Роль в организации → ключ подписи в словаре кабинета (WEB-1874). */
const ORG_ROLE_LABEL_KEYS = {
  owner: 'roleOrgOwner',
  admin: 'roleOrgAdmin',
  member: 'roleOrgMember',
} as const;

/** С этого числа организаций над списком появляется поиск (эталон: `> 5`). */
export const ORG_FIND_FROM = 6;

/**
 * Ключ подписи роли, если роль нам известна. Незнакомую роль подписи не даём
 * — выдумывать её было бы враньём про права.
 */
function orgRoleLabelKey(
  role: string,
): (typeof ORG_ROLE_LABEL_KEYS)[keyof typeof ORG_ROLE_LABEL_KEYS] | undefined {
  return ORG_ROLE_LABEL_KEYS[role as keyof typeof ORG_ROLE_LABEL_KEYS];
}

export function OrgSwitcher({
  onCreateClick,
  onSelect,
  onToast,
}: OrgSwitcherProps) {
  const s = useStrings(settingsStrings);
  const c = useStrings(commonStrings);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { orgs, activeOrgId } = useAppSelector((state) => state.organizations);
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shownOrgs = [
    ...orgs.filter((o) => o.id === activeOrgId),
    ...orgs.filter((o) => o.id !== activeOrgId),
  ].filter((o) => !needle || o.name.toLowerCase().includes(needle));

  // WEB-1564: у одного человека может быть не больше десяти организаций
  // (потолок плоский, от тарифа не зависит). Считаем ровно то, что считает
  // сервер, — СВОЁ ВЛАДЕНИЕ: чужие организации, куда позвали участником или
  // администратором, в потолок не идут ни там, ни здесь.
  const ownedCount = orgs.filter((o) => o.role === 'owner').length;
  const atOrgLimit = ownedCount >= MAX_ORGS_PER_USER;
  /**
   * WEB-1894 п.3 (находка дорожки Д4): причина у погашенной двери называла
   * предел и путь дальше, но не ТЕКУЩЕЕ ЧИСЛО — человек читал «не больше 10»
   * и не понимал, сколько организаций у него сейчас и какие из них считаются.
   * Число складывается ЗДЕСЬ, а не внутри `ORG_LIMIT_REACHED_MESSAGE`: та
   * строка — зеркало серверной байт-в-байт под сторожем паритета
   * (api/__tests__/serverErrorMessages.test.ts), и сервер числа не называет.
   * Считается своё владение — ровно то, чем гасится дверь.
   *
   * WEB-3817: строка — на языке кабинета (прежде английский кабинет читал
   * «Organizations 10 of 10. Достигнут предел…»).
   *
   * WEB-4091 (решение владельца 30.10 ~20:15): короткая строка эталона —
   * «Организаций N из M. Достигнут предел.», без длинного правила сервера;
   * M — настоящий предел `MAX_ORGS_PER_USER`, не число в словаре.
   *
   * WEB-4117 (решение владельца 28.10 «текст отказа на пределе 10 своих
   * организаций — короткий, по эталону», `orgMaxS` app.jsx:160, 178): полное
   * правило сервера во всплывашку не дописывается, оно остаётся отказу сервера.
   */
  const orgLimitReason = s.orgLimitReason(ownedCount, MAX_ORGS_PER_USER);
  // WEB-329: persist the active org on the server before updating local state.
  // On 403 (not a member) the store is NOT updated and an error toast is shown.
  const handleOrgClick = async (orgId: string) => {
    // WEB-2910: смена организации снимает текущий проект РАНЬШЕ смены адреса —
    // блокировщик роутера на странице с несохранёнными правками опаздывает.
    // Сперва спрашиваем страницу через общую дверь, как переключатель проекта.
    if (!(await confirmLeave())) return;
    // WEB-759/WEB-761: «гостевая» орга (role='member' — доступ только через
    // членство в её проектах). BE теперь разрешает персист такой орги
    // (WEB-761 AC-1); если бэкенд ещё старый и 403-ит — тихо переключаемся
    // локально (без сырого тоста). Ведём в /projects: орг-дашборд для гостя
    // всегда 403, сайдбар орг-страницы прячет (AC-3).
    const target = orgs.find((o) => o.id === orgId);
    if (target?.role === 'member') {
      const persist = await dispatch(switchActiveOrgThunk(orgId));
      // WEB-1368 п.1.10: сам локальный запасной путь — осознанный компромисс
      // (WEB-759/WEB-761) и остаётся как был. Чинится ТОЛЬКО сообщение:
      // раньше при отказе сервера человек видел зелёное «Рабочее пространство
      // переключено» и не знал, что выбор не сохранён и после перезагрузки
      // его вернёт назад.
      const persisted = switchActiveOrgThunk.fulfilled.match(persist);
      if (!persisted) {
        dispatch(setActiveOrg(orgId));
      }
      dispatch(clearActiveProjectThunk());
      dispatch(fetchProjectsThunk(orgId));
      dispatch(fetchBillingThunk(undefined));
      // WEB-2466: то же правило посадки, что у «Вся организация» и посадки
      // после перезагрузки — одна копия (orgModeLanding), не две.
      navigate(orgModeLanding(target.role));
      if (persisted) {
        onToast(s.workspaceSwitched, 'ok');
      } else {
        onToast(s.workspaceSwitchedLocally, 'warn');
      }
      return;
    }
    const result = await dispatch(switchActiveOrgThunk(orgId));
    if (switchActiveOrgThunk.fulfilled.match(result)) {
      // WEB-377: drop the previous org's selected project FIRST — the projects
      // reducer prefers an existing currentProject (`state.currentProject ?? …`),
      // so without this the old-org project lingers after the switch. Then load
      // the new org's projects and refresh the balance for the new context.
      // WEB-499: BE-persist this org-mode entry too (DELETE /user/active-project)
      // so a reload after an org switch restores org-mode, not the old project.
      dispatch(clearActiveProjectThunk());
      dispatch(fetchProjectsThunk(orgId));
      dispatch(fetchBillingThunk(undefined));
      // WEB-407 ①: land on the org dashboard — after a switch we're in org-mode
      // (currentProject cleared), so the URL must follow or content stays stale.
      navigate(orgModeLanding(target?.role));
      onToast(s.workspaceSwitched, 'ok');
    } else {
      // WEB-1955: разобранный отказ уходит в реестр кодов целиком — приведение
      // к строке молча выбрасывало и код, и причину.
      onToast(localizeError(result.payload, s.workspaceSwitchFailed), 'err');
    }
  };

  return (
    <>
      <li className="pop-gh" data-testid="org-switcher">
        {c.organizations}
      </li>
      {orgs.length >= ORG_FIND_FROM && (
        <li role="none" className="me-find">
          <input
            className="input"
            type="search"
            placeholder={c.orgFind}
            aria-label={c.orgFind}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="me-org-find"
          />
        </li>
      )}
      <li role="none" className="me-orgs">
        <ul role="group" aria-label={c.organizations} data-testid="me-orgs">
          {shownOrgs.map((org) => {
            const isActive = org.id === activeOrgId;
            const roleKey = orgRoleLabelKey(org.role);
            return (
              <li
                key={org.id}
                role="menuitemradio"
                aria-checked={isActive}
                tabIndex={0}
                className="opt me-org"
                data-testid="me-org"
                data-org-id={org.id}
                onClick={() => {
                  onSelect?.();
                  void handleOrgClick(org.id);
                }}
              >
                <span className="me-o">
                  <span>{org.name}</span>
                  {/* Роль у каждой организации (WEB-641 A3) и число проектов, если
                      сервер его отдал. Незнакомой роли подписи нет. */}
                  {roleKey && (
                    <small data-testid={`org-role-label-${org.id}`}>
                      {s[roleKey]}
                      {typeof org.projectCount === 'number'
                        ? ` · ${s.projectsCount(org.projectCount)}`
                        : ''}
                    </small>
                  )}
                </span>
                {isActive && (
                  <span
                    className="me-check"
                    data-testid={`org-active-${org.id}`}
                  >
                    <Icon name="check" size={14} />
                  </span>
                )}
              </li>
            );
          })}
          {shownOrgs.length === 0 && (
            <li role="none" className="me-none" data-testid="me-org-none">
              {c.orgNone}
            </li>
          )}
        </ul>
      </li>
      {/* WEB-3758 (владелец 27.10): на пределе пункт тот же, отказ — всплывашкой
          ошибки оболочки, окно создания не открывается. Предел считается по
          своему владению, ровно как у сервера (WEB-1564). */}
      <li
        role="menuitem"
        tabIndex={0}
        className="opt"
        data-testid="me-create-ws"
        onClick={() => {
          if (atOrgLimit) {
            onSelect?.();
            onToast(orgLimitReason, 'err');
            return;
          }
          onCreateClick();
        }}
      >
        <span>
          <Icon name="plus" size={14} /> {s.createWorkspace}
        </span>
      </li>
    </>
  );
}
